import { useCallback, useState } from 'react';
import {
  AccountType,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  ButtonWidth,
  FAIcon,
  IconStyle,
  NextgenImage,
  Shade,
  Theme,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import { useSelector, useDispatch } from 'react-redux';
import { type AppState, type AppDispatch } from '@nextgen-web-framework/core';
import {
  DocumentImageDataWithTypeTypeEnum,
  DynamicObjectInterface,
  RegFlowConfig,
  updateRegistrationData,
} from '@commerce-profile-webcomponents/sdk';
import DesktopLayout from '../Common/DesktopLayout/DesktopLayout';
import { SponsorCard } from '../RegistrationSponsor/SponsorCard/SponsorCard';
import TermsAndConditionCheckBox from '../Common/TermsAndConditionCheckBox/TermsAndConditionCheckBox';
import { convertDateObject, getJWTToken, storeRegistrationStepBack } from '../../utils/registrationUtils';
import { useCountryMasterData } from '../../utils/masterdata';
import { COAPP_REGISTRATION_STEP_NAME, CONSTANTS, DATE_FORMAT } from '../../utils/Constants';
import { StepType } from '../Registration/types/propTypes';
import { useAddressFormat } from '../../utils/myAccountAddressUtils';
import IdentityDrawer from './IdentityDrawer';
import { RegistrationPersonalPreviewStepProps } from './types/propTypes';
import styles from './RegistrationPersonalPreviewStep.module.scss';

const RegistrationPreviewStep = ({ nextHandler }: RegistrationPersonalPreviewStepProps) => {
  const t = useTranslation();
  const [showUsernameDrawer, setShowUsernameDrawer] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const personalDetailsImage = accountTypeImagesCmsData?.[selectedAccountType]?.personalDetailsImage;
  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const termsNConditionsCmsData = useSelector((state: AppState) => state.commerceProfile.termsNConditionsCMS);
  const termsAndConditions = termsNConditionsCmsData?.[selectedAccountType]?.termsAndConditions;
  const patchVersion = registrationFlowData?.userData?.patchVersion;
  const address = registrationFlowData?.perosonalDetails?.address;
  const addrFormat = useAddressFormat;
  const formattedAddress = addrFormat(address);
  const { sampleCid, sampleCidBack } = registrationCmsData;
  const isPhonePrimaryContact = registrationFlowData?.phonePrimaryContactSession;
  const flowName = registrationFlowData?.flowName;
  const userName = registrationFlowData?.perosonalDetails?.userName;
  const flowSteps: StepType[] = useSelector((state: AppState) => state.commerceProfile.currentFlowSteps);
  const { isAnonymous } = useSession();
  const isAboDownlineFlow = flowName === RegFlowConfig.AboLedFlow && isAnonymous();
  const isAboUplineFlow = flowName === RegFlowConfig.AboLedFlow && !isAnonymous();
  const { currentCountry } = useCountryMasterData();
  const defaultCountryDialCode = `+${currentCountry?.phoneCode}`;

  const nextHandlerFn = useCallback(
    (payload = {}) =>
      nextHandler({ version: patchVersion, ...payload }, undefined, false, isAboUplineFlow ? false : true),
    [patchVersion, registrationFlowData?.flowName],
  );

  const createIdentityFn = async (password: string) => {
    const userNamePayload = isPhonePrimaryContact ? defaultCountryDialCode + userName : userName;
    const identityJWT = await getJWTToken(userNamePayload ?? '', password);
    setShowUsernameDrawer(false);
    nextHandlerFn({ identityJWT });
  };

  const nextButtonClick = () => {
    isAboDownlineFlow ? setShowUsernameDrawer(true) : nextHandlerFn();
  };

  const editPersonalDetailsRedirect = (stepType: string | undefined) => {
    const currentStep = flowSteps[registrationFlowData.currentIndex];
    const editStepNameKey = stepType === COAPP_REGISTRATION_STEP_NAME ? 'stepNameForCoappEdit' : 'stepNameForEdit';
    const nextStepNameConfig = currentStep?.config?.[selectedAccountType]?.find(
      (configData) => configData.key === editStepNameKey,
    )?.value;
    let nextIndex = flowSteps?.findIndex((flowStep) => flowStep.stepName === nextStepNameConfig);
    nextIndex = nextIndex === -1 ? registrationFlowData.currentIndex - 1 : nextIndex;
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        currentIndex: nextIndex,
      }),
    );
    storeRegistrationStepBack(nextIndex);
  };

  const PersonalDetailSection = ({
    perosonalDetails,
    documentDataList,
    isPhonePrimaryContactFlag,
  }: DynamicObjectInterface) => {
    const frontImage = documentDataList?.find(
      (documentData: { type: DocumentImageDataWithTypeTypeEnum }) =>
        documentData?.type === DocumentImageDataWithTypeTypeEnum.Front,
    )?.['documentData'];
    const backImage = documentDataList?.find(
      (documentData: { type: DocumentImageDataWithTypeTypeEnum }) =>
        documentData?.type === DocumentImageDataWithTypeTypeEnum.Back,
    )?.['documentData'];
    const secondaryContactInfo = isPhonePrimaryContactFlag ? perosonalDetails?.email : perosonalDetails?.phone;
    return (
      <>
        <div className={styles['registration-personal-preview-box']}>
          <div className={styles['registration-personal-preview-content']}>
            <div className={styles['info-title']} data-testid={'info-title'}>
              {t('profile.ProfileInfo.personalIdType')}
            </div>
            <div className={styles['info-detail']} data-testid={'info-detail'}>
              {t(`profile.ProfileInfo.personalIdType.${perosonalDetails?.personalIdType}`) || '-'}
            </div>
          </div>
          <div className={styles['registration-personal-preview-content']}>
            <div className={styles['info-title']} data-testid={'info-title'}>
              {t('profile.ProfileInfo.personalIdRecordNumber')}
            </div>
            <div className={styles['info-detail']} data-testid={'info-detail'}>
              {perosonalDetails?.personalIdRecordNumber || '-'}
            </div>
          </div>
        </div>

        {(backImage || frontImage) && (
          <div className={`${styles['registration-personal-preview-box']} ${styles['image-container']}`}>
            <div>
              <NextgenImage
                src={frontImage ? `data:image/jpeg;base64,${frontImage}` : sampleCid?.url}
                alt={sampleCid?.altText as string}
                height={CONSTANTS.IMAGE_HEIGHT}
                width={CONSTANTS.IMAGE_WIDTH}
                fill={false}
                objectFit="contain"
              />
            </div>
            <div>
              <NextgenImage
                src={backImage ? `data:image/jpeg;base64,${backImage}` : sampleCidBack?.url}
                alt={sampleCid?.altText as string}
                height={CONSTANTS.IMAGE_HEIGHT}
                width={CONSTANTS.IMAGE_WIDTH}
                fill={false}
                objectFit="contain"
              />
            </div>
          </div>
        )}

        <div className={`${styles['registration-personal-preview-box']} ${styles['no-border']}`}>
          <div className={styles['registration-personal-preview-content']}>
            <div className={styles['info-title']} data-testid={'info-title'}>
              {t('profile.RegistrationPersonalDetailsStep.firstName')}
            </div>
            <div className={styles['info-detail']} data-testid={'info-detail'}>
              {perosonalDetails?.firstName || '-'}
            </div>
          </div>
          <div className={styles['registration-personal-preview-content']}>
            <div className={styles['info-title']} data-testid={'info-title'}>
              {t('profile.RegistrationPersonalDetailsStep.lastName')}
            </div>
            <div className={styles['info-detail']} data-testid={'info-detail'}>
              {perosonalDetails?.lastName || '-'}
            </div>
          </div>
        </div>

        {perosonalDetails?.taxRegime && (
          <div className={styles['registration-personal-preview-box']}>
            <div className={styles['registration-personal-preview-content']}>
              <div className={styles['info-title']} data-testid={'info-title'}>
                {t('profile.RegistrationUserDetailsStep.taxRegime')}
              </div>
              <div className={styles['info-detail']} data-testid={'info-detail'}>
                {t(`profile.ProfileInfo.taxRegime.${perosonalDetails?.taxRegime}`)}
              </div>
            </div>
            {selectedAccountType !== AccountType.Customer && (
              <div className={styles['registration-personal-preview-content']}>
                <div className={styles['info-title']} data-testid={'info-title'}>
                  {t('profile.ProfileInfo.citizenshipCountry')}
                </div>
                <div className={styles['info-detail']} data-testid={'info-detail'}>
                  {t(`profile.RegistrationIdVerificationStep.country.${perosonalDetails?.citizenshipCountry}`)}
                </div>
              </div>
            )}
          </div>
        )}
        {perosonalDetails?.birthDate && perosonalDetails?.gender && (
          <div className={styles['registration-personal-preview-box']}>
            <div className={styles['registration-personal-preview-content']}>
              <div className={styles['info-title']} data-testid={'info-title'}>
                {t('profile.RegistrationPersonalDetailsStep.birthDate')}
              </div>
              <div className={styles['info-detail']} data-testid={'info-detail'}>
                {convertDateObject(perosonalDetails?.birthDate).format(DATE_FORMAT['DD/MM/YYYY'])}
              </div>
            </div>
            <div className={styles['registration-personal-preview-content']}>
              <div className={styles['info-title']} data-testid={'info-title'}>
                {t('profile.ProfileInfo.gender')}
              </div>
              <div className={styles['info-detail']} data-testid={'info-detail'}>
                {t(`profile.ProfileInfo.gender.${perosonalDetails?.gender}`)}
              </div>
            </div>
          </div>
        )}

        <div className={styles['registration-personal-preview-box']}>
          <div className={styles['registration-personal-preview-content-full']}>
            <div className={styles['info-title']} data-testid={'info-username'}>
              {isPhonePrimaryContactFlag
                ? t('profile.registration.username.mobileText')
                : t('profile.contactInformationForm.email')}
            </div>
            <div className={styles['info-detail']} data-testid={'info-detail'}>
              {isPhonePrimaryContactFlag
                ? `${defaultCountryDialCode}${perosonalDetails?.userName}`
                : perosonalDetails?.userName}
            </div>
            <div className={styles['info-title']} data-testid={'info-title'}>
              <i>{t('profile.RegistrationPreviewStep.amwayIdCreationText')}</i>
            </div>
          </div>
        </div>

        {secondaryContactInfo && (
          <div className={styles['registration-personal-preview-box']}>
            <div className={styles['registration-personal-preview-content-full']}>
              <div className={styles['info-title']} data-testid={'info-title'}>
                {isPhonePrimaryContactFlag
                  ? t('profile.contactInformationForm.email')
                  : t('profile.registration.username.mobileText')}
              </div>
              <div className={styles['info-detail']} data-testid={'info-detail'}>
                {secondaryContactInfo}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const EditDetailButton = ({ stepType }: { stepType?: string | undefined }) => (
    <div
      className={`${styles['registration-personal-preview-box']} ${styles['edit-personal-details']} ${
        stepType === COAPP_REGISTRATION_STEP_NAME && styles['no-border']
      }`}
    >
      <button onClick={() => editPersonalDetailsRedirect(stepType)} data-testid={'previous-step-button'}>
        <FAIcon
          theme={Theme.PURPLE}
          iconName={'pen'}
          shade={Shade.DARK}
          iconStyle={IconStyle.REGULAR}
          className={styles['icon']}
        />
        &nbsp;&nbsp;
        {stepType === COAPP_REGISTRATION_STEP_NAME
          ? t('profile.RegistrationPreviewStep.editCoAppDetails')
          : t('profile.RegistrationPreviewStep.editPersonalDetails')}
      </button>
    </div>
  );

  return (
    <DesktopLayout desktopImage={personalDetailsImage} imageClassName={styles['desktop-image']}>
      <div
        className={styles['registration-personal-preview-wrapper']}
        data-testid="registration-personal-preview-wrapper"
      >
        <div
          className={styles['registration-personal-preview-container']}
          data-testid="registration-personal-preview-container"
        >
          <div
            className={styles['registration-personal-preview-heading']}
            data-testid="registration-personal-preview-heading"
          >
            {t('profile.RegistrationPreviewStep.reviewPersonalDetails')}
          </div>

          <PersonalDetailSection
            perosonalDetails={registrationFlowData?.perosonalDetails}
            documentDataList={registrationFlowData?.documentDataList}
            isPhonePrimaryContactFlag={registrationFlowData?.phonePrimaryContactSession}
          />

          {address && address?.addrLineOne && (
            <div className={styles['registration-personal-preview-box']}>
              <div className={styles['registration-personal-preview-content-full']}>
                <div className={styles['info-title']} data-testid={'info-title'}>
                  {t('profile.RegistrationPersonalDetailsStep.primaryAddress')}
                </div>
                <div className={styles['info-detail']} data-testid={'info-detail'}>
                  {formattedAddress}
                </div>
              </div>
            </div>
          )}

          <div className={`${styles['registration-personal-preview-box']} ${styles['sponsor-personal-preview-box']}`}>
            <div className={styles['registration-personal-preview-content']}>
              <div className={styles['info-title']} data-testid={'info-title'}>
                {t('profile.RegistrationPersonalDetailsStep.sponsorHeading')}
              </div>
              <div className={styles['sponsor-detail-container']} data-testid={'sponsor-detail-container'}>
                <SponsorCard abo={{ ...registrationFlowData?.selectSponsor?.sponsor }} isDisplay={true} />
              </div>
            </div>
            {Object.keys(registrationFlowData?.selectSponsor?.internationalSponsor ?? {}).length !== 0 && (
              <div className={styles['registration-personal-preview-content']}>
                <div className={styles['info-title']} data-testid={'info-title'}>
                  {t('profile.RegistrationSponsor.internationalSponsorHeading')}
                </div>
                <div className={styles['sponsor-detail-container']} data-testid={'sponsor-detail-container'}>
                  <SponsorCard
                    abo={{ ...registrationFlowData?.selectSponsor?.internationalSponsor }}
                    isDisplay={true}
                  />
                </div>
              </div>
            )}
          </div>
          <div className={`${styles['registration-personal-preview-box']} ${styles['no-border']}`}>
            <div className={styles['tnc-checkbox-wrapper']}>
              <TermsAndConditionCheckBox
                tncText={t('profile.RegistrationIdVerificationStep.checkboxLabel')}
                tncLinkText={t('profile.RegistrationIdVerificationStep.TNCcheckbox')}
                tncContent={termsAndConditions}
                disabled={true}
                checked={true}
              />
            </div>
          </div>

          <EditDetailButton />

          {registrationFlowData?.coAppPersonalDetails?.userName && (
            <>
              <div
                className={styles['registration-personal-preview-heading']}
                data-testid="registration-personal-preview-heading"
              >
                {t('profile.RegistrationPreviewStep.reviewCoAppDetails')}
              </div>
              <PersonalDetailSection
                perosonalDetails={registrationFlowData?.coAppPersonalDetails}
                documentDataList={registrationFlowData?.coAppPersonalDetails?.documentDataList}
                isPhonePrimaryContactFlag={registrationFlowData?.coAppPersonalDetails?.phonePrimaryContactSession}
              />
              <EditDetailButton stepType={COAPP_REGISTRATION_STEP_NAME} />
            </>
          )}

          <div className={styles['submit-btn']}>
            {
              <Button
                title={
                  isAboDownlineFlow
                    ? t('profle.downlineRegistrationStep.createPassword')
                    : t('profile.common.nextButton')
                }
                type={ButtonTypes.BUTTON}
                onClick={nextButtonClick}
                data-testid="submit-btn-testid"
                buttonStyle={ButtonStyles.SOLID}
                size={ButtonSize.MEDIUM}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            }
          </div>
          {showUsernameDrawer && (
            <IdentityDrawer setShowUsernameDrawer={setShowUsernameDrawer} nextHandlerFn={createIdentityFn} />
          )}
        </div>
      </div>
    </DesktopLayout>
  );
};
export default RegistrationPreviewStep;
